import React from 'react';

import { Card } from 'src/component/ds/ui/card';
import { Pill } from 'src/component/ds/ui/pill';
import { Skeleton } from 'src/component/ds/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/component/ds/ui/tooltip';
import { cn } from 'src/component/ds/utils';
import { isWhitelabel } from 'src/utils/utils';
import { useTranslation } from 'src/i18n';

export interface DeviceSummaryBarProps {
  /** Devices under a lock-out/tag-out note. */
  loto?: number;
  offline?: number;
  alarming?: number;
  /** Every circuit matching the current filters; the "of" in "8 of 129". */
  totalDevices?: number;
  forcedOn?: number;
  onLotoClick?: () => void;
  onOfflineClick?: () => void;
  onAlarmingClick?: () => void;
  onForcedOnClick?: () => void;
  /** Swaps the pills for skeletons while the counts are being fetched. */
  loading?: boolean;
  className?: string;
}

const SKELETON_WIDTHS = ['ds-w-[85px]', 'ds-w-[93px]', 'ds-w-[124px]', 'ds-w-[110px]'];

interface SummaryPill {
  labelKey: string;
  hintKey: string;
  testId: string;
  value: React.ReactNode;
  onClick?: () => void;
}


export default function DeviceSummaryBar ({
  loto = 0,
  offline = 0,
  alarming = 0,
  totalDevices = 0,
  forcedOn = 0,
  onLotoClick,
  onOfflineClick,
  onAlarmingClick,
  onForcedOnClick,
  loading = false,
  className
}: DeviceSummaryBarProps) {
  const { t } = useTranslation();

  /* The hints name the product, which is white-labelled on atcom.plus. */
  const product = isWhitelabel() ? 'ATCOM' : 'SmartTrace';

  const pills: SummaryPill[] = [
    {
      labelKey: 'dashboard.loto',
      hintKey: 'dashboard.lotoDevicesHint',
      testId: 'device-summary-loto',
      value: loto,
      onClick: onLotoClick
    },
    {
      labelKey: 'common.offline',
      hintKey: 'dashboard.offlineDevicesHint',
      testId: 'device-summary-offline',
      value: offline,
      onClick: onOfflineClick
    },
    {
      labelKey: 'dashboard.alarming',
      hintKey: 'dashboard.alarmingDevicesHint',
      testId: 'device-summary-alarming',
      value: `${alarming}/${totalDevices}`,
      onClick: onAlarmingClick
    },
    {
      labelKey: 'dashboard.forcedOn',
      hintKey: 'dashboard.forcedOnDevicesHint',
      testId: 'device-summary-forced-on',
      value: forcedOn,
      onClick: onForcedOnClick
    }
  ];

  return (
    <Card className={cn('ds-shadow-ds-xs', className)} data-testid='device-summary-bar'>
      <div className='ds-flex ds-items-center ds-gap-2 ds-py-[13.5px] ds-pl-4 ds-pr-[19px]'>
        <p className='ds-text-ds-mini ds-font-bold ds-text-muted-foreground ds-whitespace-nowrap'>
          {t('devices.devices')}
        </p>

        {loading
          ? SKELETON_WIDTHS.map((width) => (
            <Skeleton
              key={width}
              className={cn('ds-h-ds-xs ds-rounded-full', width)}
              data-testid='device-summary-skeleton'
            />
          ))
          : (
            <TooltipProvider delayDuration={200}>
              {pills.map(({ labelKey, hintKey, testId, value, onClick }) => (
                <Tooltip key={testId}>
                  <TooltipTrigger asChild>
                    <Pill
                      label={t(labelKey)}
                      value={value}
                      testId={testId}
                      onClick={onClick}
                    />
                  </TooltipTrigger>
                  <TooltipContent>{t(hintKey, { product })}</TooltipContent>
                </Tooltip>
              ))}
            </TooltipProvider>
          )}
      </div>
    </Card>
  );
}
